// The one payload store of this page — `payloadStore.ts` wired to the browser.
//
// Imports nothing from the app on purpose: `bootstrap.ts` loads this, awaits `hydratePayloadStore()`,
// and only then imports the app (`main.tsx`). Anything this file imported would run its module
// code before the cache is filled.

import { createPayloadStore, type FlushResult, type HydrationReport, type Messenger, type PayloadMessage } from './payloadStore';
import { idbPayloadBackend } from './payloadIdb';

const CHANNEL_NAME = 'storydress-payloads';

function browserLocalStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

const hasIndexedDb = (() => {
  try {
    return typeof indexedDB !== 'undefined' && typeof window !== 'undefined';
  } catch {
    return false;
  }
})();

function broadcastMessenger(): Messenger | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  let channel: BroadcastChannel | null = null;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
  } catch {
    return null;
  }
  const ch = channel;
  return {
    post: (message: PayloadMessage) => ch.postMessage(message),
    listen: (handler: (message: PayloadMessage) => void) => {
      ch.onmessage = (event: MessageEvent) => handler(event.data as PayloadMessage);
    },
  };
}

/** Two tabs opening the first time must not both migrate the same key at once. */
async function withMigrationLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? (navigator as Navigator & { locks?: LockManager }).locks : undefined;
  if (!locks?.request) return fn();
  return locks.request('storydress-payload-migration', fn) as Promise<T>;
}

export const SAVE_FAILED_EVENT = 'storydress:save-failed';

export const payloadStore = createPayloadStore({
  local: browserLocalStorage,
  backend: hasIndexedDb ? idbPayloadBackend() : null,
  messenger: hasIndexedDb ? broadcastMessenger() : null,
  withLock: withMigrationLock,
  onEvent: (event) => {
    if (typeof window === 'undefined') return;
    if (event.type === 'remote-change') {
      // Listeners written for localStorage (`PublishScreen`) hear another tab's write as before.
      try {
        window.dispatchEvent(new StorageEvent('storage', { key: event.key }));
      } catch {
        /* very old browser: the visibilitychange backstop still re-reads */
      }
      return;
    }
    window.dispatchEvent(new CustomEvent(SAVE_FAILED_EVENT, { detail: { key: event.key, error: event.error } }));
  },
});

let hydration: Promise<HydrationReport> | null = null;

/** Fills the cache from IndexedDB and moves anything left in localStorage. Idempotent. */
export function hydratePayloadStore(): Promise<HydrationReport> {
  if (!hydration) {
    hydration = payloadStore.hydrate().then((report) => {
      if (report.moved.length || report.kept.length) {
        console.info(
          `[payloads] ${report.records} payload(s) in IndexedDB; moved ${report.moved.length} from localStorage` +
            (report.kept.length ? `, ${report.kept.length} kept there: ${report.kept.map((k) => `${k.key} (${k.reason})`).join('; ')}` : '') +
            ` — ${report.ms} ms`,
        );
      }
      return report;
    });
  }
  return hydration;
}

export function flushPayloads(): Promise<FlushResult> {
  return payloadStore.flush();
}

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  // The page may go away before a write commits — copy what is still in flight into localStorage.
  window.addEventListener('pagehide', () => payloadStore.journalPending());
  // For the Playwright check scripts (`scripts/*-check.mjs`, `render-project.mjs`), which used to
  // read and seed these keys through `localStorage` directly. Same data any same-origin script can
  // already read from IndexedDB; nothing app code uses.
  (window as unknown as { __storydressPayloads?: unknown }).__storydressPayloads = {
    get: (key: string) => payloadStore.getItem(key),
    set: (key: string, value: string) => payloadStore.setItem(key, value),
    remove: (key: string) => payloadStore.removeItem(key),
    keys: () => payloadStore.keys(),
    flush: () => payloadStore.flush(),
    mode: () => payloadStore.mode(),
  };
}
